import {
    Dialog,
    DialogContent
} from '@/components/ui/dialog'   
import { TableData } from '@/components/Table/type'
import { createColumnHelper } from '@tanstack/react-table'
import Table from '@/components/Table'
import { useEmployeeQuery, useUpdateEmployeeResearchStatusMutation } from '@/queries/useEmployeeQuery'
import { useEffect, useMemo } from 'react' 
import DefaultColumn from '@/components/Table/components/DefaultColumn' 
import { Employee } from '@/types/Employee'
import { useTable } from '@/hooks/useTable'  
import DadosMestreHeader from '../../../components/DadosMestreHeader' 
import DadosMestreHeaderActions from '../../../components/DadosMestreHeaderActions'  
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import { useBaseStore } from '@/store'
import { authStateSelector } from '@/store/authSlice'
import { decodeJwt } from 'jose'

export type EmployeeTable = TableData<Employee>

const columnHelper = createColumnHelper<Employee>()

interface EmployeeDialogProps {
    SK_REVENDA: number
    isOpen: boolean
    onClose: () => void
}

const EmployeeDialog = ({ SK_REVENDA, isOpen, onClose }: EmployeeDialogProps) => {

    const {
        table,
        tableState,
        selectedRows,  
        onClickMultiDelete, 
        setTable,  
    } = useTable<Employee>('') 

    const { toast } = useToast()
    const { token } = useBaseStore(authStateSelector)
    const decodedToken = useMemo(() => {
        if (!token) return null
        try {
            return decodeJwt(token) as any
        } catch {
            return null
        }
    }, [token])

    const perfil = decodedToken?.usuario?.perfil || decodedToken?.perfil
    const isResaleAdmin = perfil === 'ADMINISTRADOR_REVENDA'

    const { data, isFetching, isPending, refetch } = useEmployeeQuery(SK_REVENDA, true)
    const updateMutation = useUpdateEmployeeResearchStatusMutation()

    useEffect(() => {   
        if (isOpen && SK_REVENDA) {
            refetch()
        }
    }, [SK_REVENDA, isOpen, refetch])

    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [ 
            columnHelper.accessor('NK_FUNCIONARIO', {
                id: 'NK_FUNCIONARIO',
                header: 'Código',
                size: 100,
            }), 
            columnHelper.accessor('NM_FUNCIONARIO', {
                id: 'NM_FUNCIONARIO',
                header: 'Funcionário',
                size: 340,
            }), 
            columnHelper.accessor('DD_TIPO_VENDEDOR', {
                id: 'DD_TIPO_VENDEDOR',
                header: 'Status',
                size: 140,
            }),  
            columnHelper.accessor('DD_CARGO', {
                id: 'DD_CARGO',
                header: 'Cargo',
                size: 140,
            }),  
            columnHelper.accessor('DD_PARTICIPA_PESQUISA', {
                id: 'DD_PARTICIPA_PESQUISA',
                header: 'Participa da Pesquisa',
                size: 180,
                cell: ({ row }) => {
                    const isParticipating = row.original.DD_PARTICIPA_PESQUISA !== 0
                    return (
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={isParticipating}
                                disabled={isResaleAdmin || updateMutation.isPending}
                                title={isResaleAdmin ? 'Apenas Administrador Central pode alterar. (RN004)' : ''}
                                onCheckedChange={(checked) => {
                                    updateMutation.mutate(
                                        {
                                            SK_FUNCIONARIO: row.original.SK_FUNCIONARIO,
                                            DD_PARTICIPA_PESQUISA: checked ? 1 : 0,
                                        },
                                        {
                                            onSuccess: () => {
                                                toast({
                                                    title: 'Sucesso',
                                                    description: 'Status de participação em pesquisa atualizado!',
                                                })
                                            },
                                            onError: (err: any) => {
                                                toast({
                                                    title: 'Erro',
                                                    description: err?.response?.data?.message || 'Falha ao atualizar status.',
                                                    variant: 'destructive',
                                                })
                                            },
                                        }
                                    )
                                }}
                            />
                            <span className="text-xs text-muted-foreground">
                                {isParticipating ? 'Sim' : 'Não'}
                            </span>
                        </div>
                    )
                },
            }),
        ],
        [isResaleAdmin, updateMutation, toast]
    )

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[60vw] h-3/4 overflow-auto'> 
                <div className="w-full h-full p-4 overflow-hidden">
                    <Table<EmployeeTable>
                        data={memoData}
                        columns={memoColumns}
                        getRowId={(row) => row.SK_FUNCIONARIO.toString()}
                        isLoading={isPending}
                        isFetching={isFetching}
                        getTableInstance={(table) => setTable(table)}   
                        tableState={tableState}  
                        tableHeader={
                            <DadosMestreHeader
                                title={`Funcionários`}
                                onDelete={onClickMultiDelete}
                                selectedRows={
                                    table
                                        ?.getFilteredRowModel()
                                        .flatRows.filter((row) => selectedRows[row.id])
                                        .length
                                }
                            />
                        }
                        tableActions={
                            <DadosMestreHeaderActions 
                                onRefech={refetch} 
                            />
                        }  
                        defaultColumn={{
                            cell: ({ getValue }) => (
                                <DefaultColumn>{getValue() as string}</DefaultColumn>
                            ),
                        }}
                    /> 
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default EmployeeDialog
